<script setup lang="ts">
import {
    computed,
    onMounted,
    ref,
} from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useSchoolStore } from '@/stores/school';
import { useUserStore } from '@/stores/user';
import LogOutModal from '@/components/Utilities/LogOutModal.vue';
import FsumNavButton from '@/components/Navigation/FsumNavButton.vue';
import part61Icon from '@/assets/icons/61icon.svg';
import part141Icon from '@/assets/icons/141icon.svg';
import adminCenterIcon from '@/assets/icons/admin-center-icon.svg';
import { availableParts, type AvailableParts } from '@/types/Certificate';
import AdminRepository from '@/repositories/AdminRepository';
import { getUserRoleForSchool } from '@/types/FlightSchoolUser';
import { usePartStore } from '@/stores/part';

const props = defineProps<{
    isAdminCenter: boolean;
}>();

const userStore = useUserStore();
const schoolStore = useSchoolStore();

const { school } = storeToRefs(schoolStore);
const currentUserRole = computed(() => {
    if (props.isAdminCenter) {
        return userStore.printableUserRole;
    }
    return getUserRoleForSchool(userStore, school?.value?.id ?? 0);
});
const showLogOutModal = ref(false);
const route = useRoute();
const partStore = usePartStore();

const currentPart = computed(() => {
    const partFromRoute = Number(route.params.part) as AvailableParts;
    const isPartValid = availableParts.includes(partFromRoute);

    if (route.params.part && isPartValid) {
        return partFromRoute;
    }

    return partStore.getPartOrUseDefault(school?.value);
});

const getCurrentAdminInfo = async (): Promise<{ name: string, email: string }> => {
    if (userStore.userName === '' && userStore.user !== null) {
        const admin = await AdminRepository.getCurrentAdminInfo();
        userStore.setUserInfo(admin.name, admin.email);
    }
    return { name: userStore.userName, email: userStore.userEmail };
};

const icons: Record<typeof availableParts[number], string> = {
    141: part141Icon,
    61: part61Icon,
};

const iconRoute = computed(() => (
    props.isAdminCenter ? adminCenterIcon : icons[currentPart.value as typeof availableParts[number]]
));
const adminInfo = ref<{ name: string, email: string }>({ name: '', email: '' });

onMounted(async () => {
    adminInfo.value = await getCurrentAdminInfo();
});

</script>
<template>
    <q-btn-dropdown
        class="nav-main-dropdown q-py-sm full-width"
        align="between"
        :aria-label="`${isAdminCenter ? 'Admin Center' : `${school?.name} part ${currentPart} navigation menu`}`"
        dropdown-icon="sym_o_keyboard_arrow_down"
        flat
        no-caps
    >
        <template #label>
            <div class="nav-main-dropdown-label row no-wrap items-center">
                <q-icon
                    left
                    :name="'img:' + iconRoute"
                    size="34px"
                />
                <div
                    class="nav-main-dropdown-label-text"
                    :title="isAdminCenter ? 'Admin Center' : school?.name"
                >
                    {{ isAdminCenter ? 'Admin Center' : school?.name }}
                </div>
            </div>
        </template>
        <div class="nav-menu q-gutter-y-md q-px-md q-pt-md">
            <div class="admin-info">
                <div class="admin-type">
                    {{ currentUserRole }}
                </div>
                <div class="admin-name nav-subtitle">
                    {{ adminInfo.name }}
                </div>
                <div class="admin-email">
                    {{ adminInfo.email }}
                </div>
            </div>
            <slot name="menu-items" />
            <FsumNavButton
                role="menuitem"
                label="Log Out"
                icon="logout"
                font-size="14px"
                @click="showLogOutModal = true"
            />
        </div>
    </q-btn-dropdown>
    <LogOutModal
        v-model="showLogOutModal"
    />
</template>
<style lang="scss">
.nav-main-dropdown {
    border: 1px solid var(--fsum-gray4);

    .nav-main-dropdown-label-text {
        white-space: normal;
        font-weight: 500;
    }
    .nav-main-dropdown-label-text {
        display: -webkit-box !important;
        line-height: normal;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-align: left;
    }
}

.nav-menu {
    max-width: 290px;

    .admin-type {
        font-size: 11px;
        font-weight: 700;
        color: var(--fsum-gray3);
        text-transform: uppercase;
    }
    .nav-subtitle {
        font-size: 16px;
        color: var(--fsum-black);
    }
    .admin-email {
        font-size: 13px;
        font-weight: 500;
        color: var(--fsum-black);
    }
}
</style>
